import React, { useState, useMemo, useRef, useEffect } from "react";
import "./ListViewMac.css";

export interface ListViewMacColumn<T = any> {
  key: string;
  label: string;
  width?: number | string; // Can be number (px) or string (e.g., "1fr", "200px")
  minWidth?: number;
  maxWidth?: number;
  align?: "left" | "center" | "right";
  render?: (item: T, index: number) => React.ReactNode;
  sortable?: boolean;
}

export interface ListViewMacProps<T = any> {
  data: T[];
  columns: ListViewMacColumn<T>[];
  maxItems?: number; // Maximum number of items to display
  onItemClick?: (item: T, index: number) => void;
  onItemDoubleClick?: (item: T, index: number) => void;
  selectedIndex?: number | null;
  onSelectionChange?: (index: number | null) => void;
  emptyMessage?: string;
  className?: string;
  rowHeight?: number; // Height of each row in pixels
  showHeader?: boolean; // Show/hide column headers
  resizableColumns?: boolean; // Allow column resizing
}

function ListViewMac<T = any>({
  data,
  columns,
  maxItems,
  onItemClick,
  onItemDoubleClick,
  selectedIndex,
  onSelectionChange,
  emptyMessage = "No items",
  className = "",
  rowHeight = 20,
  showHeader = true,
  resizableColumns = false,
}: ListViewMacProps<T>) {
  const [resizingColumn, setResizingColumn] = useState<string | null>(null);
  const [columnWidths, setColumnWidths] = useState<Map<string, number>>(
    new Map(),
  );
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const headerRef = useRef<HTMLDivElement>(null);
  const resizeStartX = useRef<number>(0);
  const resizeStartWidth = useRef<number>(0);

  // Limit displayed items
  const displayedData = useMemo(() => {
    if (maxItems && maxItems > 0) {
      return data.slice(0, maxItems);
    }
    return data;
  }, [data, maxItems]);

  // Calculate column widths
  const gridTemplateColumns = useMemo(() => {
    return columns
      .map((col) => {
        const customWidth = columnWidths.get(col.key);
        if (customWidth) {
          return `${customWidth}px`;
        }
        if (col.width) {
          return typeof col.width === "number" ? `${col.width}px` : col.width;
        }
        return "1fr";
      })
      .join(" ");
  }, [columns, columnWidths]);

  // Handle column sorting
  const sortedData = useMemo(() => {
    if (!sortColumn) return displayedData;

    const column = columns.find((col) => col.key === sortColumn);
    if (!column || !column.sortable) return displayedData;

    return [...displayedData].sort((a, b) => {
      const aVal = (a as any)[sortColumn];
      const bVal = (b as any)[sortColumn];

      if (aVal === bVal) return 0;

      const comparison =
        aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [displayedData, sortColumn, sortDirection, columns]);

  // Handle column header click for sorting
  const handleHeaderClick = (column: ListViewMacColumn<T>) => {
    if (!column.sortable) return;

    if (sortColumn === column.key) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column.key);
      setSortDirection("asc");
    }
  };

  // Handle column resize
  const handleResizeStart = (
    e: React.MouseEvent,
    columnKey: string,
    currentWidth: number,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setResizingColumn(columnKey);
    resizeStartX.current = e.clientX;
    resizeStartWidth.current = currentWidth;
  };

  useEffect(() => {
    if (!resizingColumn) return;

    const handleMouseMove = (e: MouseEvent) => {
      const diff = e.clientX - resizeStartX.current;
      let newWidth = Math.max(
        50, // Minimum width
        resizeStartWidth.current + diff,
      );

      const column = columns.find((col) => col.key === resizingColumn);
      if (column) {
        if (column.maxWidth) {
          newWidth = Math.min(newWidth, column.maxWidth);
        }
        if (column.minWidth) {
          newWidth = Math.max(newWidth, column.minWidth);
        }
      }

      setColumnWidths((prev) => {
        const next = new Map(prev);
        next.set(resizingColumn, newWidth);
        return next;
      });
    };

    const handleMouseUp = () => {
      setResizingColumn(null);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [resizingColumn, columns]);

  // Get column width for display
  const getColumnWidth = (column: ListViewMacColumn<T>): number => {
    const customWidth = columnWidths.get(column.key);
    if (customWidth) return customWidth;
    if (typeof column.width === "number") return column.width;
    return 150; // Default width
  };

  // Handle row click
  const handleRowClick = (item: T, index: number) => {
    onSelectionChange?.(index);
    onItemClick?.(item, index);
  };

  // Handle row double click
  const handleRowDoubleClick = (item: T, index: number) => {
    onItemDoubleClick?.(item, index);
  };

  return (
    <div className={`list-view-mac ${className}`}>
      {showHeader && (
        <div
          ref={headerRef}
          className="list-view-mac-header"
          style={{ gridTemplateColumns }}
        >
          {columns.map((column, colIndex) => {
            const isSorting = sortColumn === column.key;
            const width = getColumnWidth(column);

            return (
              <div
                key={column.key}
                className={`list-view-mac-header-cell ${
                  column.sortable ? "sortable" : ""
                } ${column.align || "left"}`}
                style={{ width: resizableColumns ? `${width}px` : undefined }}
                onClick={() => handleHeaderClick(column)}
              >
                <span className="list-view-mac-header-label">
                  {column.label}
                </span>
                {isSorting && (
                  <span className="list-view-mac-sort-indicator">
                    {sortDirection === "asc" ? "▲" : "▼"}
                  </span>
                )}
                {resizableColumns && colIndex < columns.length - 1 && (
                  <div
                    className="list-view-mac-resize-handle"
                    onMouseDown={(e) =>
                      handleResizeStart(e, column.key, width)
                    }
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="list-view-mac-body">
        {sortedData.length === 0 ? (
          <div className="list-view-mac-empty">{emptyMessage}</div>
        ) : (
          sortedData.map((item, index) => {
            const isSelected = selectedIndex === index;

            return (
              <div
                key={index}
                className={`list-view-mac-row ${isSelected ? "selected" : ""}`}
                style={{
                  gridTemplateColumns,
                  height: `${rowHeight}px`,
                }}
                onClick={() => handleRowClick(item, index)}
                onDoubleClick={() => handleRowDoubleClick(item, index)}
              >
                {columns.map((column) => {
                  const width = getColumnWidth(column);

                  return (
                    <div
                      key={column.key}
                      className={`list-view-mac-cell ${column.align || "left"}`}
                      style={{
                        width: resizableColumns ? `${width}px` : undefined,
                      }}
                    >
                      {column.render
                        ? column.render(item, index)
                        : String((item as any)[column.key] ?? "")}
                    </div>
                  );
                })}
              </div>
            );
          })
        )}
      </div>

      {maxItems && data.length > maxItems && (
        <div className="list-view-mac-footer">
          Showing {maxItems} of {data.length} items
        </div>
      )}
    </div>
  );
}

export default ListViewMac;
